
import { Ball, SimulationConfig } from '../../types';
import { resolveBallCollision } from './BallCollision';

export class PhysicsSystem {
  private grid: Map<string, number[]> = new Map();

  constructor() {}

  // Optimized Ball-Ball Collision Resolution using Spatial Hashing
  resolveBallCollisions(
      balls: Ball[], 
      config: SimulationConfig,
      onCollision?: (b1: Ball, b2: Ball, impact: number) => void
  ) {
    // Dynamic Cell Size based on max radius to ensure large objects don't miss collisions
    // We use a minimum of 40 for performance with small balls
    const maxR = config.mode === 'SPACE_SPIRAL' ? (config.spaceSpiralBoxSize || 30) : (config.ballMaxRadius || 40);
    const cellSize = Math.max(40, maxR * 2.5); // Ensure cell covers diameter + margin
    
    this.grid.clear();
    
    // Insert balls into grid
    for (let i = 0; i < balls.length; i++) {
      const b = balls[i];
      const cellKey = this.getCellKey(b.pos.x, b.pos.y, cellSize);
      
      let cell = this.grid.get(cellKey);
      if (!cell) {
        cell = [];
        this.grid.set(cellKey, cell);
      }
      cell.push(i);
    }

    // Check collisions within cells and neighbors
    for (let i = 0; i < balls.length; i++) {
      const b1 = balls[i];
      const cellX = Math.floor(b1.pos.x / cellSize);
      const cellY = Math.floor(b1.pos.y / cellSize);

      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          const key = `${cellX + dx},${cellY + dy}`;
          const neighbors = this.grid.get(key);
          
          if (neighbors) {
            for (const j of neighbors) {
              if (i >= j) continue; // Avoid duplicate checks and self-check
              
              const b2 = balls[j];

              // SPACE SPIRAL:
              // If both are spiral boxes (index > 0) and both are "static" (low velocity), skip collision.
              // This prevents the initial "blast" if they overlap slightly on spawn.
              // But we allow collisions if at least one is moving (debris mode).
              if (config.mode === 'SPACE_SPIRAL' && i > 0 && j > 0) {
                  const s1 = b1.vel.x*b1.vel.x + b1.vel.y*b1.vel.y;
                  const s2 = b2.vel.x*b2.vel.x + b2.vel.y*b2.vel.y;
                  if (s1 < 0.01 && s2 < 0.01) continue;
              }

              const restitution = config.mode === 'SPACE_SPIRAL' ? config.spaceSpiralRestitution : config.restitution;
              const impact = resolveBallCollision(b1, b2, restitution);
              
              if (impact > 0 && onCollision) {
                  onCollision(b1, b2, impact);
              }
            }
          }
        }
      }
    }
  }

  private getCellKey(x: number, y: number, cellSize: number): string {
    const cellX = Math.floor(x / cellSize);
    const cellY = Math.floor(y / cellSize);
    return `${cellX},${cellY}`;
  }
}
